import { Controller } from "@hotwired/stimulus"

// The duplicate-link "Heads up!" modal on the release forms. The TRUTH is
// server-side (ReleaseLinkGuard re-checks on every submit); this only opens
// the modal the 422 re-render shipped, and on Yes echoes the holder ids into
// the hidden field and resubmits the enclosing form. Cancel closes it and
// hands focus to the Spotify field so the paste can be fixed in place.
// Escape and the backdrop cancel; Tab is trapped inside the dialog.
// Initial focus lands on CANCEL, not Yes (committee 2026-09-02, Seat 2):
// this dialog exists because the paste is probably a mistake, and a held
// Enter from the Save that triggered the re-render must never confirm a
// duplicate unread. Yes stays the visually primary button.
export default class extends Controller {
  static targets = ["confirmed", "modal"]
  static values = { open: Boolean }

  connect() {
    if (this.openValue && this.hasModalTarget) this.open()
  }

  disconnect() {
    if (this.hasModalTarget) this.modalTarget.removeEventListener("keydown", this.trapHandler)
  }

  open() {
    this.modalTarget.hidden = false
    this.trapHandler = (e) => this.trapFocus(e)
    this.modalTarget.addEventListener("keydown", this.trapHandler)
    const cancel = this.modalTarget.querySelector("button[data-action*='#cancel']")
    if (cancel) cancel.focus()
  }

  cancel() {
    this.close()
    const field = this.form()?.querySelector("input[name$='[spotify_url]']")
    if (field) field.focus()
  }

  proceed(event) {
    this.confirmedTarget.value = event.currentTarget.dataset.holderIds
    this.close()
    this.form()?.requestSubmit()
  }

  close() {
    this.modalTarget.hidden = true
    this.modalTarget.removeEventListener("keydown", this.trapHandler)
  }

  trapFocus(event) {
    if (event.key === "Escape") return this.cancel()
    if (event.key !== "Tab") return
    const buttons = Array.from(this.modalTarget.querySelectorAll("button"))
    const first = buttons[0]
    const last = buttons[buttons.length - 1]
    // A click on the panel body parks focus on a non-button; the next Tab
    // must stay inside the dialog rather than walk out to the page.
    if (!buttons.includes(document.activeElement)) {
      event.preventDefault(); return first.focus()
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  form() {
    return this.element.closest("form")
  }
}
